/**
 * RecordPaymentModal — used on both Invoices (money IN) and Bills (money OUT).
 *
 * Shamil's ask (2026-06-24): "include a button on the invoice, where when we
 * click on it, we enter the amount received, the bank we received the money to,
 * and the date. This in turn gets picked up to the bank reconciliation."
 *
 * The backend already updates invoice/bill paidCents + status atomically when
 * a payment is recorded (see backend/src/routes/payments.ts), and the
 * matching bank_transaction is created when statements are uploaded — so just
 * recording the payment with a bankAccountId here is enough to wire the
 * receivable into the debtor report and the bank rec flow.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RupeeInput } from "../lib/RupeeInput";
import { fmtCents, paymentsApi, bankApi } from "../lib/business";
import { todayLocalISO } from "../lib/dates";

export function RecordPaymentModal({
  direction,
  invoiceId,
  billId,
  remainingCents,
  onClose,
  onPaid,
}: {
  direction: "in" | "out";
  invoiceId?: string;
  billId?: string;
  remainingCents: string;
  onClose: () => void;
  onPaid: () => void;
}) {
  const [amountCents, setAmountCents] = useState<number>(Number(remainingCents));
  const [date, setDate] = useState(todayLocalISO());
  const [reference, setReference] = useState("");
  const [bankAccountId, setBankAccountId] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bank accounts list — drives the "received to" / "paid from" picker so
  // the payment links to a real bank for reconciliation.
  const { data: bankData } = useQuery({
    queryKey: ["bank-accounts"],
    queryFn: () => bankApi.listAccounts(),
    staleTime: 60_000,
  });
  const banks = bankData?.accounts ?? [];

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (amountCents <= 0) { setError("Amount must be greater than zero."); return; }
    if (amountCents > Number(remainingCents)) {
      setError(`Amount exceeds the remaining balance (${fmtCents(remainingCents)}).`);
      return;
    }
    setSubmitting(true);
    try {
      await paymentsApi.create({
        direction,
        invoiceId: invoiceId ?? null,
        billId: billId ?? null,
        bankAccountId: bankAccountId || null,
        paymentDate: date,
        amountCents: String(amountCents),
        reference: reference || null,
      });
      onPaid();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not record payment.");
    } finally {
      setSubmitting(false);
    }
  }

  const incoming = direction === "in";
  return (
    <div
      style={{
        position: "fixed", inset: 0,
        background: "rgba(14,22,18,0.5)",
        display: "grid", placeItems: "center",
        zIndex: 100,
      }}
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="card card-pad-lg modal-card"
        style={{ width: 420, maxWidth: "90vw", display: "flex", flexDirection: "column", gap: 14 }}
      >
        <div style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 500 }}>
          {incoming ? "Record payment received" : "Record payment made"}
        </div>
        <div className="form-field">
          <label className="form-label">Amount (LKR)</label>
          <RupeeInput cents={amountCents} onChange={setAmountCents} />
          <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4 }}>
            Outstanding: {fmtCents(remainingCents)}
          </div>
        </div>
        <div className="form-field">
          <label className="form-label">Date</label>
          <input className="form-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="form-field">
          <label className="form-label">{incoming ? "Received to bank account" : "Paid from bank account"}</label>
          {banks.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--ink-3)", padding: "8px 10px", border: "0.5px dashed var(--line)", borderRadius: 6 }}>
              No bank accounts yet — add one under <strong>Bank</strong> first so payments link to your reconciliation.
            </div>
          ) : (
            <select
              className="form-input"
              value={bankAccountId}
              onChange={(e) => setBankAccountId(e.target.value)}
            >
              <option value="">— Cash / unallocated —</option>
              {banks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.accountName} · {b.accountNumber} ({b.bankCode})
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="form-field">
          <label className="form-label">Reference</label>
          <input
            className="form-input"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="e.g. cheque no, bank transfer ref"
          />
        </div>
        {error && <div className="banner banner-error">{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Saving…" : "Record →"}
          </button>
        </div>
      </form>
    </div>
  );
}
